// Binary Search — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/stack.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer clarifying the halving/partition logic.

const binarySearchProblems = [
  {
    id: 'search-in-rotated-sorted-array',
    title: 'Search in Rotated Sorted Array',
    difficulty: 'Medium',
    leetcode: { number: 33, url: 'https://leetcode.com/problems/search-in-rotated-sorted-array/' },
    statement:
      'An array, originally sorted in ascending order with no duplicates, has been rotated at some unknown ' +
      'pivot. Given the rotated array and a target value, return the target\'s index, or −1 if it isn\'t present.',
    example: {
      input: 'nums = [4, 5, 6, 7, 0, 1, 2], target = 0',
      output: '4',
      note: 'The array was [0,1,2,4,5,6,7] before being rotated left by 4 positions.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Linear Scan)',
        why:
          "The direct reading of the problem: check every element until the target is found. Correct — a rotated " +
          "array is still just an array — but it throws away the fact that most of it is still locally sorted.",
        steps: [
          'Walk the array from index 0.',
          'Compare each element to the target.',
          'Return the index the moment a match is found.',
          'Every element gets checked in the worst case — O(n).',
        ],
        code:
`public int searchBruteForce(int[] nums, int target) {
    for (int i = 0; i < nums.length; i++) {
        if (nums[i] == target) return i;
    }
    return -1;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        whenToUse:
          "Only useful to confirm the problem's shape out loud — the moment you notice the array is rotated " +
          "sorted rather than random, that's a direct signal a modified binary search applies.",
      },
      {
        name: 'Approach 2 — Optimized: Binary Search, Picking the Sorted Half',
        why:
          "A rotated sorted array has one guarantee that survives the rotation: at ANY midpoint, at least one of " +
          "the two halves (left of mid, or right of mid) is still properly sorted — the rotation \"break point\" " +
          "can only live in one of them. Check which half is sorted, then check whether the target falls inside " +
          "that sorted half's range; if it does, search there, otherwise the target must be in the other half.",
        steps: [
          'Set lo = 0 and hi = nums.length − 1. Compute mid.',
          'If nums[mid] equals the target, return mid.',
          'Determine which half is properly sorted by comparing nums[lo] to nums[mid].',
          'If the target\'s value falls within that sorted half\'s range, search there; otherwise, search the other half.',
        ],
        visual: {
          arrays: [{ label: 'nums (target = 0)', values: [4, 5, 6, 7, 0, 1, 2] }],
          varNames: [],
          steps: [
            { highlights: { nums: [3] }, note: 'lo=0, hi=6, mid=3: nums[3]=7 ≠ 0. Left half [4,5,6,7] is sorted (nums[0]=4 ≤ nums[3]=7), but 0 isn\'t between 4 and 7 — search the right half. lo = 4.' },
            { highlights: { nums: [5] }, note: 'lo=4, hi=6, mid=5: nums[5]=1 ≠ 0. Left half [0,1] is sorted (nums[4]=0 ≤ nums[5]=1), and 0 IS between 0 and 1 — search there. hi = 4.' },
            { highlights: { nums: [4] }, note: 'lo=4, hi=4, mid=4: nums[4]=0 equals the target — found at index 4, in 3 comparisons.' },
          ],
        },
        code:
`public int search(int[] nums, int target) {
    int lo = 0, hi = nums.length - 1;
    while (lo <= hi) {
        int mid = lo + (hi - lo) / 2;
        if (nums[mid] == target) return mid;

        if (nums[lo] <= nums[mid]) {                    // left half is sorted
            if (nums[lo] <= target && target < nums[mid]) {
                hi = mid - 1;
            } else {
                lo = mid + 1;
            }
        } else {                                         // right half is sorted
            if (nums[mid] < target && target <= nums[hi]) {
                lo = mid + 1;
            } else {
                hi = mid - 1;
            }
        }
    }
    return -1;
}`,
        complexity: { time: 'O(log n)', space: 'O(1)' },
        interviewNote: "Try the \"Rotated Sorted Array\" mode in this pattern's interactive playground to run this exact algorithm on any rotated array and target you choose.",
        whenToUse:
          "Whenever an array is described as sorted-then-rotated, this is the answer — the key insight to say out " +
          "loud is that one half is ALWAYS still sorted, so checking nums[lo] vs nums[mid] is enough to know which one.",
      },
    ],
  },
  {
    id: 'median-of-two-sorted-arrays',
    title: 'Median of Two Sorted Arrays',
    difficulty: 'Hard',
    leetcode: { number: 4, url: 'https://leetcode.com/problems/median-of-two-sorted-arrays/' },
    statement:
      'Given two sorted arrays nums1 and nums2, return the median of the two arrays combined. The overall run ' +
      'time complexity should be O(log(m+n)).',
    example: {
      input: 'nums1 = [1, 2], nums2 = [3, 4]',
      output: '2.5',
      note: 'Merged and sorted, the combined array is [1,2,3,4] — the median is the average of the two middle values, (2+3)/2.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Merge Both Arrays)',
        why:
          "The direct reading of the problem: merge the two sorted arrays into one sorted array, then read the " +
          "median directly from the middle. Correct, but merging costs O(m+n) — the problem explicitly asks for " +
          "O(log(m+n)), which this doesn't come close to.",
        steps: [
          'Merge nums1 and nums2 into a single sorted array, the same way merge sort combines two sorted halves.',
          'If the combined length is odd, the median is the single middle element.',
          'If the combined length is even, the median is the average of the two middle elements.',
          'The merge itself costs O(m+n), and so does reading off the median — nowhere near O(log(m+n)).',
        ],
        code:
`public double findMedianBruteForce(int[] nums1, int[] nums2) {
    int[] merged = new int[nums1.length + nums2.length];
    int i = 0, j = 0, k = 0;
    while (i < nums1.length && j < nums2.length) {
        merged[k++] = (nums1[i] <= nums2[j]) ? nums1[i++] : nums2[j++];
    }
    while (i < nums1.length) merged[k++] = nums1[i++];
    while (j < nums2.length) merged[k++] = nums2[j++];

    int n = merged.length;
    if (n % 2 == 1) return merged[n / 2];
    return (merged[n / 2 - 1] + merged[n / 2]) / 2.0;
}`,
        complexity: { time: 'O(m + n)', space: 'O(m + n)' },
        whenToUse:
          "Only useful to confirm what \"median of the combined array\" means — the O(log(m+n)) requirement in " +
          "the problem statement is a direct signal that a full merge is not the intended solution.",
      },
      {
        name: 'Approach 2 — Optimized: Binary Search Over the Partition Point',
        why:
          "Instead of merging the values, binary search over WHERE TO SPLIT the smaller array. A valid split " +
          "point i in the smaller array (nums1) determines exactly one matching split point j in the larger " +
          "array (nums2), such that the combined left partition holds exactly half of all elements — everything " +
          "in the left partition must be ≤ everything in the right partition. Binary searching over i (a range " +
          "of at most min(m,n)+1 positions) finds that split in O(log(min(m,n))), which satisfies O(log(m+n)).",
        steps: [
          'Ensure nums1 is the smaller array (swap the two if not) — this bounds the binary search range by the smaller length.',
          'Binary search over a partition index i in nums1 (0 to m). The matching partition index j in nums2 is determined: j = (m+n+1)/2 − i.',
          'Check the four border values: maxLeftX, minRightX, maxLeftY, minRightY. If maxLeftX ≤ minRightY and maxLeftY ≤ minRightX, the partition is correct.',
          'If maxLeftY > minRightX, i needs to grow (search higher). If maxLeftX > minRightY, i needs to shrink (search lower). Once correct, compute the median from the four border values.',
        ],
        visual: {
          arrays: [{ label: 'nums1', values: [1, 2] }, { label: 'nums2', values: [3, 4] }],
          varNames: [],
          steps: [
            { highlights: { nums1: { primary: [0], secondary: [1] }, nums2: { primary: [0], secondary: [1] } }, note: 'i=1, j=1: left partition = {1} ∪ {3}, right partition = {2} ∪ {4}. maxLeftY=3 > minRightX=2 — this split is invalid, i needs to grow. Search higher.' },
            { highlights: { nums1: { primary: [0, 1] }, nums2: { secondary: [0, 1] } }, note: 'i=2, j=0: left partition = {1,2} ∪ {} (all of nums1, none of nums2), right partition = {} ∪ {3,4}. maxLeftX=2 ≤ minRightY=3, and maxLeftY=−∞ ≤ minRightX=+∞ — valid partition!' },
            { note: 'Combined length (4) is even: median = (max(maxLeftX, maxLeftY) + min(minRightX, minRightY)) / 2 = (max(2, −∞) + min(+∞, 3)) / 2 = (2 + 3) / 2 = 2.5.' },
          ],
        },
        code:
`public double findMedianSortedArrays(int[] nums1, int[] nums2) {
    if (nums1.length > nums2.length) return findMedianSortedArrays(nums2, nums1);  // ensure nums1 is smaller

    int m = nums1.length, n = nums2.length;
    int lo = 0, hi = m;
    int half = (m + n + 1) / 2;

    while (lo <= hi) {
        int i = lo + (hi - lo) / 2;   // partition index in nums1
        int j = half - i;             // matching partition index in nums2

        int maxLeftX  = (i == 0) ? Integer.MIN_VALUE : nums1[i - 1];
        int minRightX = (i == m) ? Integer.MAX_VALUE : nums1[i];
        int maxLeftY  = (j == 0) ? Integer.MIN_VALUE : nums2[j - 1];
        int minRightY = (j == n) ? Integer.MAX_VALUE : nums2[j];

        if (maxLeftX <= minRightY && maxLeftY <= minRightX) {
            if ((m + n) % 2 == 0) {
                return (Math.max(maxLeftX, maxLeftY) + Math.min(minRightX, minRightY)) / 2.0;
            } else {
                return Math.max(maxLeftX, maxLeftY);
            }
        } else if (maxLeftY > minRightX) {
            lo = i + 1;   // need more elements from nums1 on the left
        } else {
            hi = i - 1;   // took too many from nums1 — back off
        }
    }
    throw new IllegalArgumentException("Input arrays are not sorted");
}`,
        complexity: { time: 'O(log(min(m, n)))', space: 'O(1)' },
        interviewNote: "The hardest part to explain clearly is WHY j = (m+n+1)/2 − i always gives a valid matching split — walk through it as: the left partition needs exactly half (rounded up) of the combined elements, and once i elements are taken from nums1, the rest of that quota must come from nums2.",
        whenToUse:
          "This is the single hardest binary-search variant in the curriculum — binary search over a PARTITION " +
          "POINT across two arrays at once, rather than over a value or a single array's indices. It's worth " +
          "revisiting after the simpler drills in this pattern feel comfortable.",
      },
    ],
  },
];

export default binarySearchProblems;
